/**
 * Owner: Masa Bayu
 * File: AdminAccountsScreen.tsx
 * Date:Time: 2026-10-23 11:20
 * Description: §467-D — senarai akaun daripada `GET /v1/admin/dashboard` (§467-B).
 *   Penapis hanya memperkecil baris yang DIHANTAR pelayan; KPI peranan pula angka
 *   hub, jadi kiraan di kepala sentiasa sama dengan jadual. Tiada kawalan
 *   peranan/daftar semula — konsol ini membaca, tidak menulis (§467d-R).
 */

import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import type { AdminLabels } from './admin-labels';
import type { AdminDashboardView, HubAdminAccount } from './admin-dashboard';
import { PAGE_SIZE, dateText, roleLabel, signInLabel } from './admin-view';

const HEAD = PAGE_SIZE;

interface AdminAccountRow extends HubAdminAccount {
  readonly emailText: string;
  readonly joinedText: string;
  readonly signInText: string;
  readonly searchKey: string;
}

function accountRow(account: HubAdminAccount, la: AdminLabels): AdminAccountRow {
  const emailText = account.email ?? account.accountId;
  return {
    ...account,
    emailText,
    joinedText: dateText(account.createdAt),
    signInText: signInLabel(account, la),
    searchKey: `${emailText} ${account.accountId} ${account.displayName} ${roleLabel(
      account.role,
      la,
    )}`.toLowerCase(),
  };
}

function matches(row: AdminAccountRow, needle: string): boolean {
  if (needle.length === 0) return true;
  return row.searchKey.includes(needle);
}

export interface AdminAccountsScreenProps {
  readonly view: AdminDashboardView;
  readonly la: AdminLabels;
}

export function AdminAccountsScreen({ view, la }: AdminAccountsScreenProps): ReactElement {
  const [needle, setNeedle] = useState('');
  const [expanded, setExpanded] = useState(false);

  const all = useMemo<readonly AdminAccountRow[]>(
    () => view.accounts.map((account) => accountRow(account, la)),
    [view.accounts, la],
  );
  const rows = useMemo<readonly AdminAccountRow[]>(() => {
    const lower = needle.trim().toLowerCase();
    return all.filter((account) => matches(account, lower));
  }, [all, needle]);

  const shown = expanded ? rows : rows.slice(0, HEAD);
  const hidden = rows.length - shown.length;
  const counts = view.counts.accounts;

  return (
    <div className="qxa__stack">
      <section className="qxa__filters" aria-label={la.accountSearchLabel}>
        <label className="qxa__field">
          <span className="qxa__field-label">{la.accountSearchLabel}</span>
          <input
            type="search"
            value={needle}
            placeholder={la.searchPlaceholder}
            onChange={(event) => {
              setNeedle(event.target.value);
              setExpanded(false);
            }}
          />
        </label>
        <p className="qxa__match">
          {String(rows.length)} {la.matchCount} · {String(counts.total)}{' '}
          {la.kpiAccounts.toLowerCase()}
        </p>
      </section>

      <ul className="qxa__counts" aria-label={la.roleBreakdown}>
        <li>
          <span className="qxa__pill qxa__pill--neutral">
            {la.roleReader} · {String(counts.reader)}
          </span>
        </li>
        <li>
          <span className="qxa__pill qxa__pill--neutral">
            {la.rolePublisher} · {String(counts.publisher)}
          </span>
        </li>
        <li>
          <span className="qxa__pill qxa__pill--warn">
            {la.roleAdmin} · {String(counts.admin)}
          </span>
        </li>
      </ul>

      <section className="qxa__card" aria-label={la.accountsTitle}>
        {rows.length === 0 ? (
          <p className="qxa__empty">{la.accountsEmpty}</p>
        ) : (
          <>
            <div className="qxa__table-wrap">
              <table className="qxa__table">
                <caption className="qxa__sr">{la.accountsTitle}</caption>
                <thead>
                  <tr>
                    <th scope="col">{la.colEmail}</th>
                    <th scope="col">{la.colRole}</th>
                    <th scope="col">{la.colBooks}</th>
                    <th scope="col">{la.colGrants}</th>
                    <th scope="col">{la.colOrders}</th>
                    <th scope="col">{la.colSince}</th>
                    <th scope="col">{la.colSignIn}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((account) => (
                    <tr key={account.accountId}>
                      <td data-label={la.colEmail}>
                        <span className="qxa__cell-title">{account.emailText}</span>
                        {account.displayName.length === 0 ? null : (
                          <span className="qxa__cell-sub">{account.displayName}</span>
                        )}
                        {account.email === null ? null : (
                          <code className="qxa__row-id">{account.accountId}</code>
                        )}
                      </td>
                      <td data-label={la.colRole}>
                        <span className="qxa__pill qxa__pill--neutral">
                          {roleLabel(account.role, la)}
                        </span>
                      </td>
                      <td data-label={la.colBooks}>{String(account.publishedCount)}</td>
                      <td data-label={la.colGrants}>{String(account.activeGrants)}</td>
                      <td data-label={la.colOrders}>{String(account.orders)}</td>
                      <td data-label={la.colSince}>{account.joinedText}</td>
                      <td data-label={la.colSignIn}>{account.signInText}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {rows.length > HEAD || expanded ? (
              <button
                type="button"
                className="qxa__btn qxa__btn--ghost qxa__more"
                onClick={() => setExpanded((value) => !value)}
              >
                {expanded ? la.showFewer : `${la.showAll} (+${String(hidden)})`}
              </button>
            ) : null}
          </>
        )}
        <p className="qxa__note">{la.roleNote}</p>
      </section>
    </div>
  );
}
